<template>
  <Navbar />
  <div class="bind-mobile">
    <div class="bind-mobile-form">
      <div class="title-section">
        <h2>{{ $t("smsAuth.bindTitle") }}</h2>
        <p class="subtitle">{{ $t("smsAuth.bindSubtitle") }}</p>
      </div>

      <a-form :model="formModel" @finish="handleSubmit">
        <a-form-item name="phone" :rules="[]">
          <label for="phone" class="form-label">
            {{ $t("smsAuth.phoneLabel") }}<span class="required">*</span>
          </label>
          <a-input
            id="phone"
            :placeholder="$t('smsAuth.phonePlaceholder')"
            v-model:value="formModel.phone"
            maxlength="15"
            :disabled="codeSent"
          />
        </a-form-item>

        <a-form-item v-if="codeSent" name="code" :rules="[]">
          <label for="code" class="form-label">
            {{ $t("smsAuth.codeLabel") }}<span class="required">*</span>
          </label>
          <a-input
            id="code"
            :placeholder="$t('smsAuth.codePlaceholder')"
            v-model:value="formModel.code"
            maxlength="6"
            @input="onCodeInput"
          />
        </a-form-item>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <a-button
          v-if="!codeSent"
          class="submit-btn"
          type="primary"
          :loading="sendLoading"
          :disabled="!isPhoneValid"
          size="large"
          block
          @click="handleSendCode"
        >
          <span>{{ $t("smsAuth.sendCode") }}</span>
        </a-button>

        <a-button
          v-else
          class="submit-btn"
          type="primary"
          :loading="loading"
          :disabled="!isCodeValid"
          size="large"
          block
          html-type="submit"
        >
          <span>{{ $t("smsAuth.bindSubmit") }}</span>
        </a-button>

        <div v-if="codeSent" class="resend-section">
          <a-button
            type="link"
            :disabled="resendCountdown > 0 || resendLoading"
            @click="handleSendCode"
          >
            <span v-if="resendLoading">Loading...</span>
            <span v-else-if="resendCountdown > 0">
              {{ $t("smsAuth.resendIn", { seconds: resendCountdown }) }}
            </span>
            <span v-else>{{ $t("smsAuth.resendCode") }}</span>
          </a-button>
        </div>
      </a-form>
    </div>
  </div>
</template>

<script setup>
import Navbar from "../components/Navbar.vue";
import { ref, computed, onMounted, onUnmounted, reactive } from "vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { authAPI } from "@/services/api.js";

const router = useRouter();
const { t: $t } = useI18n();

const formModel = reactive({
  phone: "",
  code: "",
});

const loading = ref(false);
const sendLoading = ref(false);
const resendLoading = ref(false);
const codeSent = ref(false);
const resendCountdown = ref(0);
const errorMessage = ref("");
let countdownTimer = null;

const isPhoneValid = computed(() =>
  /^[0-9]{8,15}$/.test(formModel.phone.trim())
);
const isCodeValid = computed(() => /^[0-9]{6}$/.test(formModel.code));

const onCodeInput = () => {
  formModel.code = formModel.code.replace(/\D/g, "").slice(0, 6);
  if (errorMessage.value) errorMessage.value = "";
};

const startCountdown = () => {
  resendCountdown.value = 60;
  if (countdownTimer) clearInterval(countdownTimer);
  countdownTimer = setInterval(() => {
    resendCountdown.value--;
    if (resendCountdown.value <= 0) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }, 1000);
};

const handleSendCode = async () => {
  if (!isPhoneValid.value) {
    errorMessage.value = $t("smsAuth.errorPhoneFormat");
    return;
  }

  if (codeSent.value) resendLoading.value = true;
  else sendLoading.value = true;

  try {
    await authAPI.resendMobileCode({ phone: formModel.phone.trim() });
    codeSent.value = true;
    message.success($t("smsAuth.resendSuccess") || "驗證碼已寄送");
    startCountdown();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 400) {
      // 冷卻中
      errorMessage.value = error?.response?.data?.message || $t("smsAuth.errorRateLimit");
    } else {
      errorMessage.value = error?.response?.data?.message || "寄送失敗";
    }
  } finally {
    sendLoading.value = false;
    resendLoading.value = false;
  }
};

const handleSubmit = async () => {
  if (!isCodeValid.value) {
    errorMessage.value = $t("smsAuth.errorCodeInvalid");
    return;
  }

  loading.value = true;
  try {
    const response = await authAPI.verifyMobileCode({
      phone: formModel.phone.trim(),
      code: formModel.code,
    });

    if (response.status === 200) {
      message.success($t("smsAuth.successBind"));
      // 補綁成功：嘗試取得原訪問路徑
      const lastPath = localStorage.getItem("lastVisitedPath") || "/dashboard";
      router.push(lastPath);
    } else {
      errorMessage.value = response.data?.message || $t("smsAuth.errorCodeInvalid");
    }
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || $t("smsAuth.errorCodeInvalid");
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  // 檢查是否已登入（沒 token 應導向登入頁）
  const token = localStorage.getItem("ms_platform_token");
  if (!token) {
    message.warning("請先登入");
    router.push("/sign-in");
  }
});

onUnmounted(() => {
  if (countdownTimer) clearInterval(countdownTimer);
});
</script>

<style scoped>
.bind-mobile {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: 32px 16px;
}
.bind-mobile-form {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.title-section {
  text-align: center;
  margin-bottom: 32px;
}
.subtitle {
  color: #888;
  margin-top: 8px;
}
.form-label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
}
.required {
  color: #ff4d4f;
}
.error-message {
  color: #ff4d4f;
  font-size: 14px;
  margin: 8px 0 16px;
}
.submit-btn {
  height: 48px;
  font-size: 16px;
  margin-top: 8px;
}
.resend-section {
  text-align: center;
  margin-top: 12px;
}
</style>
